import React from 'react';
import { motion } from 'framer-motion';
import { type Question } from '../../content';
import { Button } from '../../components/ui/Button';
import { AnswerComparison } from './feedback/AnswerComparison';
import { formatCorrectAnswer, formatUserAnswer } from './feedback/feedbackAnswerFormatters';
import { DjayAvatar } from '../../mascot';
import { getQuestionTypeConfig } from '../../content';
import { ArrowRight, Star } from 'lucide-react';
function isInteractiveQuestion(question?: Question): boolean {
  if (!question) return false;
  return getQuestionTypeConfig(question.questionType || 'multiple_choice').isInteractive;
}
interface FluencyCorrectProps {
  currentQuestionIndex: number;
  speedStreak: number;
  xpGained: number;
  selectedVariant: string;
}
export function FluencyCorrectState({
  currentQuestionIndex,
  speedStreak,
  xpGained,
  selectedVariant
}: FluencyCorrectProps) {
  return <motion.div key={`fluency-correct-${currentQuestionIndex}`} initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.9
  }} className="flex-1 flex flex-col items-center justify-center py-12" data-id="element-9309">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-8 md:p-12 text-center shadow-xl shadow-amber-100/50 max-w-md w-full relative" data-id="element-9310">
        {speedStreak >= 3 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-black uppercase tracking-wider px-4 py-1.5 rounded-full shadow-lg" data-id="element-9311">
            {speedStreak}x Snelheidsreeks! ⚡
          </motion.div>}
        <motion.div initial={{
        scale: 0
      }} animate={{
        scale: 1
      }} transition={{
        type: 'spring',
        bounce: 0.5,
        delay: 0.1
      }} className="mx-auto mb-6 flex justify-center" data-id="element-9312">
          <DjayAvatar size="lg" emotion="celebrating" variant={selectedVariant} data-id="element-9313" />
        </motion.div>
        <h2 className="text-2xl font-black text-amber-800 mb-2" data-id="element-9314">
          {speedStreak >= 3 ? 'Razendsnel! 🔥' : speedStreak > 0 ? 'Lekker bezig! ⚡' : 'Goed gedaan!'}
        </h2>
        {xpGained > 0 && <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-amber-100 shadow-sm mt-4" data-id="element-9315">
            <Star className="w-5 h-5 fill-amber-400 text-amber-400" data-id="element-9316" />
            <span className="font-bold text-amber-700" data-id="element-9317">+{xpGained} XP</span>
          </div>}
      </div>
    </motion.div>;
}
interface FluencyIncorrectProps {
  currentQuestionIndex: number;
  currentQuestion: Question;
  userTypedAnswer: string;
  userAnswerIndex: number | null;
  selectedVariant: string;
  onShowFullFeedback: () => void;
  onNext: () => void;
}
export function FluencyIncorrectState({
  currentQuestionIndex,
  currentQuestion,
  userTypedAnswer,
  userAnswerIndex,
  selectedVariant,
  onShowFullFeedback,
  onNext
}: FluencyIncorrectProps) {
  const interactive = isInteractiveQuestion(currentQuestion);
  const userAnswer = formatUserAnswer(currentQuestion, interactive ? userTypedAnswer : currentQuestion.options?.[userAnswerIndex ?? 0] || '');
  return <motion.div key={`fluency-incorrect-${currentQuestionIndex}`} initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} className="flex-1 flex flex-col items-center justify-center py-8 px-4" data-id="element-9318">
      <div className="bg-amber-50 border-2 border-amber-200 rounded-3xl p-6 md:p-8 shadow-xl shadow-amber-100/50 max-w-lg w-full" data-id="element-9319">
        <div className="flex items-center gap-4 mb-6" data-id="element-9320">
          <div className="w-16 h-16 flex-shrink-0" data-id="element-9321">
            <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} data-id="element-9322" />
          </div>
          <div className="bg-white rounded-2xl p-3 border border-amber-100 shadow-sm relative" data-id="element-9323">
            <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-amber-100 rotate-45" data-id="element-9324" />
            <p className="font-bold text-amber-900 text-sm md:text-base relative z-10" data-id="element-9325">
              Oeps! Dat was niet helemaal goed. Kijk even naar het juiste
              antwoord.
            </p>
          </div>
        </div>

        <div className="mb-8" data-id="element-9326">
          <AnswerComparison userAnswer={userAnswer} correctAnswer={formatCorrectAnswer(currentQuestion)} isCorrect={false} mode="compact" data-id="element-9327" />
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 justify-end" data-id="element-9328">
          <Button variant="ghost" onClick={onShowFullFeedback} className="w-full sm:w-auto text-amber-700 hover:text-amber-900 hover:bg-amber-100/50" data-id="element-9329">
            Meer uitleg
          </Button>
          <Button variant="primary" onClick={onNext} className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 border-amber-600 text-white shadow-md shadow-amber-500/20" data-id="element-9330">
            Ik snap het, volgende!
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-9331" />
          </Button>
        </div>
      </div>
    </motion.div>;
}